import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { Lock as LockIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import React from "react";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { useWithdrawRequestMutation } from "../Requests.generated.js";
import { useTranslate } from "@tolgee/react";
import { Button } from "@mui/material";

interface Props {
  requestId: string;
  projectId: string;
  needsConfirm?: boolean;
  refetchQueries?: string[];
}

export const RequestCancelButton = ({
  requestId,
  projectId,
  needsConfirm,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["Requests", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [closeRequest, { loading }] = useWithdrawRequestMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const handleCloseRequest = async () => {
    try {
      await closeRequest({
        variables: {
          input: { projectId, requestId },
        },
      });
      enqueueSnackbar(
        t("Request cancelled", {
          ns: "Requests",
        })
      );
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  if (needsConfirm) {
    return (
      <ModalOpenButton
        Modal={ConfirmModal}
        modalProps={{
          title: t("Cancel request", {
            ns: "Requests",
          }),
          text: t("Are you sure you want to cancel this request?", {
            ns: "Requests",
          }),
          confirmButtonLabel: t("Yes, cancel", {
            ns: "Requests",
          }),
          handleConfirm: handleCloseRequest,
          cancelButtonLabel: t("No", {
            ns: "Global",
          }),
        }}
      >
        <Button
          startIcon={<LockIcon />}
          variant="outlined"
          color="primary"
          loading={loading}
        >
          {t("Cancel", {
            ns: "Requests",
          })}
        </Button>
      </ModalOpenButton>
    );
  }

  return (
    <Button
      startIcon={<LockIcon />}
      variant="outlined"
      color="primary"
      loading={loading}
      onClick={handleCloseRequest}
    >
      {t("Cancel", {
        ns: "Requests",
      })}
    </Button>
  );
};
